import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  CalendarClock,
  CheckCircle2,
  XCircle,
  MessageSquarePlus,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";

const STATUS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  declined: "bg-rose-100 text-rose-800 border-rose-300",
};
const fmt = (s) => (s ? s.split("-").reverse().join("/") : "");
const fmtTime = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "");

function RequestCard({ r, quickNotes }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["leave-requests"] });
    qc.invalidateQueries({ queryKey: ["leave-pending-count"] });
  };

  const addNote = useMutation({
    mutationFn: () => api.post(`/leave-requests/${r.id}/note`, { text: note }),
    onSuccess: () => { setNote(""); refresh(); toast.success("Note added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not add note"),
  });
  const decide = useMutation({
    mutationFn: (action) => api.post(`/leave-requests/${r.id}/decision`, { action, note }),
    onSuccess: (res, action) => {
      setNote("");
      refresh();
      const n = res.data?.labels_applied;
      toast.success(action === "approved"
        ? `Approved${n ? ` — ${n} day(s) labelled on the timesheet` : ""}`
        : "Declined");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save decision"),
  });

  return (
    <Card className="rounded-md border p-4" data-testid={`leave-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-bold">{r.cleaner_name}</span>
            {r.payroll_ref ? (
              <span className="rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground" data-testid={`leave-payref-${r.id}`}>
                ref: {r.payroll_ref}
              </span>
            ) : (
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700" data-testid={`leave-noref-${r.id}`}>
                no payroll ref
              </span>
            )}
          </div>
          <div className="mt-0.5 text-sm text-muted-foreground">
            Submitted {fmtTime(r.created_at)}
          </div>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold uppercase ${STATUS[r.status] || ""}`} data-testid={`leave-status-${r.id}`}>
          {r.status}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-4">
        <div><div className="label-caps text-muted-foreground">Type</div><div className="font-semibold">{r.leave_type}</div></div>
        <div><div className="label-caps text-muted-foreground">Dates</div><div className="font-semibold">{fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}</div></div>
        <div><div className="label-caps text-muted-foreground">Total</div><div className="font-semibold">{r.total_days} day{r.total_days === 1 ? "" : "s"}</div></div>
        <div><div className="label-caps text-muted-foreground">Declared</div><div className="flex items-center gap-1 font-semibold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Yes</div></div>
      </div>

      {r.reason && (
        <div className="mt-2 rounded-md bg-secondary/50 p-2 text-sm"><span className="text-muted-foreground">Reason: </span>{r.reason}</div>
      )}
      {r.med_cert && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-indigo-700"><Stethoscope className="h-4 w-4" /> Medical certificate provided / to follow</div>
      )}

      {/* Internal manager notes — hidden from cleaners */}
      <div className="mt-3 rounded-md border border-dashed border-border p-3">
        <div className="label-caps mb-2 flex items-center gap-1.5 text-muted-foreground">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Manager notes (internal — not shown to staff)
        </div>
        {(r.manager_notes || []).length > 0 ? (
          <div className="mb-2 space-y-1.5" data-testid={`leave-notes-${r.id}`}>
            {r.manager_notes.map((n, i) => (
              <div key={`${n.at || i}-${n.by || ""}`} className="text-sm">
                <span className="font-semibold">{n.by}</span>
                <span className="text-muted-foreground"> · {fmtTime(n.at)}</span>
                <div>{n.text}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mb-2 text-sm text-muted-foreground">No notes yet. Discuss here before deciding.</div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {(quickNotes || []).map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setNote((v) => (v ? `${v} ${q}` : q))}
              data-testid={`quick-note-${q.slice(0, 8).replace(/\s/g, "-")}`}
              className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary"
            >
              {q}
            </button>
          ))}
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note, or type your own…"
          rows={2}
          className="mt-2"
          data-testid={`leave-note-input-${r.id}`}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate()} data-testid={`leave-add-note-${r.id}`}>
            {addNote.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Add note
          </Button>
          {r.status === "pending" && (
            <>
              <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={decide.isPending} onClick={() => decide.mutate("approved")} data-testid={`leave-approve-${r.id}`}>
                <CheckCircle2 className="h-4 w-4" /> Approve
              </Button>
              <Button size="sm" variant="destructive" className="gap-1" disabled={decide.isPending} onClick={() => decide.mutate("declined")} data-testid={`leave-decline-${r.id}`}>
                <XCircle className="h-4 w-4" /> Decline
              </Button>
              <span className="text-xs text-muted-foreground">Approving applies the leave label to those days.</span>
            </>
          )}
        </div>
        {r.status !== "pending" && (
          <div className="mt-2 text-xs text-muted-foreground" data-testid={`leave-decision-${r.id}`}>
            {r.status === "approved" ? "Approved" : "Declined"} by {r.decided_by} · {fmtTime(r.decided_at)}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function LeaveRequests() {
  const [filter, setFilter] = useState("pending");
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["leave-requests"],
    queryFn: async () => (await api.get("/leave-requests")).data,
  });
  const { data: qn } = useQuery({
    queryKey: ["leave-quick-notes"],
    queryFn: async () => (await api.get("/leave-quick-notes")).data,
  });
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["leave-settings"],
    queryFn: async () => (await api.get("/leave-settings")).data,
  });
  const emailToggle = useMutation({
    mutationFn: (enabled) => api.put("/leave-settings", { email_enabled: enabled }),
    onSuccess: (_r, enabled) => {
      qc.invalidateQueries({ queryKey: ["leave-settings"] });
      toast.success(enabled ? "Email notifications on" : "Email notifications off");
    },
  });

  const shown = filter === "all" ? requests : requests.filter((r) => r.status === filter);
  const counts = {
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.status === "approved").length,
    declined: requests.filter((r) => r.status === "declined").length,
  };

  return (
    <div className="space-y-5" data-testid="leave-requests-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Leave requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review staff leave applications. Notes are private to managers; approving applies the leave label to the timesheet.
          </p>
        </div>
        <label className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm" data-testid="leave-email-toggle-row">
          <Switch
            checked={!!settings?.email_enabled}
            onCheckedChange={(v) => emailToggle.mutate(v)}
            data-testid="leave-email-toggle"
          />
          Email managers on new requests
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ["pending", `Pending (${counts.pending})`],
          ["approved", `Approved (${counts.approved})`],
          ["declined", `Declined (${counts.declined})`],
          ["all", "All"],
        ].map(([k, label]) => (
          <Button
            key={k}
            variant={filter === k ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(k)}
            data-testid={`leave-filter-${k}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : shown.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 rounded-md border border-dashed p-10 text-center text-muted-foreground" data-testid="leave-empty">
          <CalendarClock className="h-8 w-8" />
          No {filter === "all" ? "" : filter} leave requests.
        </Card>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => (<RequestCard key={r.id} r={r} quickNotes={qn?.notes} />))}
        </div>
      )}
    </div>
  );
}
